import React, { useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useRootStore } from "../../store/contexts";

export const TwoFaVerifyForm = observer(() => {
  const rootStore = useRootStore();
  const auth = rootStore.authStore;
  const lang = rootStore.langStore;
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [error, setError] = useState("");

  const handleVerify = async () => {
    setError("");
    try {
      if (useBackupCode) {
        await auth.verifyTwoFaBackupCode();
      } else {
        await auth.verifyTwoFa();
      }
    } catch (e: any) {
      setError(lang.twoFaInvalidCode || "Incorrect code, please try again");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleVerify();
    }
  };

  const toggleMode = () => {
    setUseBackupCode(!useBackupCode);
    auth.setTwoFaCode("");
    setError("");
  };

  return (
    <Paper elevation={10} sx={{ p: 3, maxWidth: 360, mx: "auto", mt: 3 }}>
      <Typography variant="h6" align="center" gutterBottom>
        {lang.twoFaVerifyTitle || "Two-Factor Authentication"}
      </Typography>

      <Typography variant="body2" sx={{ mb: 2 }} align="center">
        {useBackupCode
          ? (lang.twoFaEnterBackupCode || "Enter one of your backup codes")
          : (lang.twoFaEnterAppCode || "Enter the 6-digit code from your authenticator app")}
      </Typography>

      <TextField
        fullWidth
        label={useBackupCode
          ? (lang.twoFaEnterBackupCode || "Backup code")
          : (lang.twoFaEnterCode || "6-digit code")}
        value={auth.twoFaCode}
        onChange={(e) => auth.setTwoFaCode(e.target.value)}
        onKeyDown={handleKeyDown}
        inputProps={useBackupCode ? {} : { maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
        sx={{ mb: 2 }}
        autoFocus
      />

      {error && (
        <Typography color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {/* Opt-in: recordar este dispositivo para saltar 2FA por 10 días.
          Este form solo se muestra cuando el dispositivo NO está confiado, así
          que la opción nunca aparece en dispositivos ya recordados. */}
      <FormControlLabel
        sx={{ mb: 1 }}
        control={
          <Checkbox
            checked={auth.rememberDevice}
            onChange={(e) => auth.setRememberDevice(e.target.checked)}
          />
        }
        label={lang.twoFaRememberDevice || "Recordar este dispositivo por 10 días"}
      />

      <Button
        variant="contained"
        fullWidth
        onClick={handleVerify}
        disabled={!useBackupCode && auth.twoFaCode.length !== 6}
        sx={{ mb: 1 }}
      >
        {lang.twoFaVerifyButton || "Verify"}
      </Button>

      <Box sx={{ textAlign: "center", mt: 1 }}>
        <Link component="button" variant="body2" onClick={toggleMode}>
          {useBackupCode
            ? (lang.twoFaUseAppCode || "Use authenticator app")
            : (lang.twoFaUseBackupCode || "Use a backup code")}
        </Link>
      </Box>
    </Paper>
  );
});
